import React, { useState, useEffect, useMemo } from 'react';
import { Archetype, Companion, UserProfile, ApiKeyStatus, GameCartridge, MultiplayerRoom } from '@/types'; // Import ApiKeyStatus
// Import MicrophoneIcon and adjust companion icons
import { RobotIcon, BrainIcon, AlertTriangleIcon, LoadingSpinnerIcon } from './icons'; // Added AlertTriangleIcon, LoadingSpinnerIcon
import { INITIAL_COMPANIONS } from '@/constants';
import { databaseService } from '@/services/databaseService';
import { isFeatureEnabled } from '@/utils/featureFlags';
import { WorldMapCanvas } from '@/components/WorldMapCanvas';

import { generateOptimizedImage } from '@/services/optimizedImageService';

interface MainScreenProps {
  onStartGame: (archetype: Archetype) => void;
  apiKeyStatus: ApiKeyStatus; // Changed from apiKeyExists
  userProfile: UserProfile;
  archetypes: Archetype[];
  selectedCartridge?: GameCartridge | null;
  currentRoom?: MultiplayerRoom | null;
  isMultiplayer?: boolean;
}

const MainScreen: React.FC<MainScreenProps> = ({ onStartGame, apiKeyStatus, userProfile, archetypes = [], selectedCartridge, currentRoom, isMultiplayer }) => {
  const [selectedArchetype, setSelectedArchetype] = useState<Archetype | null>(null);
  const [playerNames, setPlayerNames] = useState<Record<string, string>>({});
  const [loadingPlayerNames, setLoadingPlayerNames] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const [loadingHeroImage, setLoadingHeroImage] = useState(true);
  const [isStartingGame, setIsStartingGame] = useState(false);
  const worldMapEnabled = useMemo(() => isFeatureEnabled('worldMap') || import.meta.env.DEV, []);
  
  // Use cartridge archetypes if in multiplayer mode, otherwise use default archetypes
  const displayArchetypes: Archetype[] = (
    isMultiplayer && selectedCartridge && Array.isArray(selectedCartridge.archetypes)
      ? selectedCartridge.archetypes
      : Array.isArray(archetypes)
        ? archetypes
        : []
  );
  const cartridgeName = isMultiplayer && selectedCartridge ? selectedCartridge.name : 'Neo-Dream Weaver';
  const companions = (
    isMultiplayer && selectedCartridge && Array.isArray(selectedCartridge.companions)
      ? selectedCartridge.companions
      : Array.isArray(INITIAL_COMPANIONS)
        ? INITIAL_COMPANIONS
        : []
  );

  // Generate hero image based on cartridge
  useEffect(() => {
    const generateHeroImage = async () => {
      try {
        setLoadingHeroImage(true);
        const { generateImage } = await import('@/services/openRouterToElevenLabsAdapter');
        
        let imagePrompt = selectedCartridge?.storyPrompts?.initialImagePrompt || 
          'Dark cyberpunk cityscape, Neo-Veridia arcology, neon lights, atmospheric fog, futuristic architecture, high contrast, cinematic lighting, detailed digital art';
        
        // Replace template variables with actual values
        imagePrompt = imagePrompt
          .replace(/{playerName}/g, userProfile?.name || userProfile?.displayName || 'the traveler')
          .replace(/{playerAge}/g, userProfile?.age?.toString() || '25');
        
        const imageUrl = await generateOptimizedImage(imagePrompt, {
          width: 800,
          height: 400,
          seed: Math.floor(Math.random() * 1000000),
          type: 'scene'
        });
        
        setHeroImageUrl(imageUrl);
      } catch (error) {
        console.error('Failed to generate hero image:', error);
        // Use a fallback gradient if image generation fails
        setHeroImageUrl(null);
      } finally {
        setLoadingHeroImage(false);
      }
    };
    
    generateHeroImage();
  }, [selectedCartridge?.id, userProfile?.uid]);

  // Fetch player names when currentRoom changes
  useEffect(() => {
    const fetchPlayerNames = async () => {
      if (!currentRoom || !currentRoom.currentPlayers || currentRoom.currentPlayers.length === 0) {
        return;
      }

      setLoadingPlayerNames(true);
      const names: Record<string, string> = {};
      
      try {
        // currentRoom.currentPlayers is an array of UIDs (strings)
        const playerIds = Array.isArray(currentRoom.currentPlayers) ? currentRoom.currentPlayers : [];
        
        for (const playerId of playerIds) {
          if (typeof playerId === 'string') {
            try {
              const profile = await databaseService.getUserProfile(playerId);
              names[playerId] = profile?.name || profile?.displayName || 'Anonymous';
            } catch (error) {
              console.warn(`Failed to fetch profile for player ${playerId}:`, error);
              names[playerId] = 'Unknown';
            }
          }
        }
        
        setPlayerNames(names);
      } catch (error) {
        console.error('Error fetching player names:', error);
      } finally {
        setLoadingPlayerNames(false);
      }
    };

    if (currentRoom && currentRoom.currentPlayers) {
      fetchPlayerNames();
    }
  }, [currentRoom]);

  if (apiKeyStatus === 'unknown' || apiKeyStatus === 'checking') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 p-4 text-center">
        <LoadingSpinnerIcon className="w-12 h-12 text-cyan-400 mb-4" />
        <p className="text-slate-300 text-lg">
          {apiKeyStatus === 'checking' ? "Verifying API Key..." : "Initializing..."}
        </p>
      </div>
    );
  }

  if (apiKeyStatus === 'not_exists') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 p-4 text-center">
        <AlertTriangleIcon className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-5xl font-orbitron font-bold text-red-500 neon-text-fuchsia mb-4">System Configuration Error</h1>
        <p className="text-slate-300 text-lg">System is not properly configured.</p>
         <p className="text-slate-400 text-sm mt-2">
          Please contact support if this issue persists. The game cannot start in the current state.
        </p>
      </div>
    );
  }
  
  // apiKeyStatus === 'exists'
  const CompanionDisplay: React.FC<{companion: Companion, archetype: Archetype | null, userProfile: UserProfile | null}> = ({ companion, archetype, userProfile }) => {
    const dreamerName = userProfile?.name || "Dreamer";
    let introLine = "Awaiting dream sequence initiation...";
    if (archetype) {
      if (companion.name === "JAX") introLine = `A ${archetype.name}, huh, ${dreamerName}? Let's see if you can keep up with the data stream.`;
      if (companion.name === "Lyra") introLine = `The path of a ${archetype.name}... an interesting thread in the dream's tapestry, ${dreamerName}.`;
    } else {
        if (companion.name === "JAX") introLine = `JAX online. Systems nominal, ${dreamerName}. Ready when you are.`;
        if (companion.name === "Lyra") introLine = `Lyra attuned. The dream awaits your consciousness, ${dreamerName}.`;
    }
    
    const Icon = companion.name === "JAX" ? RobotIcon : BrainIcon;
    // Default avatar colors if not defined
    const avatarColor = companion.avatarColor || (companion.name === "JAX" ? "text-cyan-400" : "text-fuchsia-400");
    const neonColor = avatarColor ? avatarColor.split('-')[1] : "cyan";

    return (
    <div className="flex items-start space-x-3 p-3 glassmorphism rounded-lg border border-slate-700/30 hover:border-cyan-500/50 transition-all">
      <Icon className={`w-10 h-10 ${avatarColor} flex-shrink-0 mt-1`} />
      <div>
        <h4 className={`text-lg font-semibold ${avatarColor} neon-text-${neonColor}`}>{companion.name}</h4>
        <p className="text-xs text-slate-400 italic text-base-responsive">{introLine}</p>
      </div>
    </div>
  )};

  // Helper function to get player names for display
  const getPlayerNamesText = () => {
    if (!currentRoom || !currentRoom.currentPlayers) return '';
    
    if (loadingPlayerNames) return 'Loading player names...';
    
    const playerIds = Array.isArray(currentRoom.currentPlayers) ? currentRoom.currentPlayers : [];
    return playerIds.map(playerId => {
      if (typeof playerId === 'string') {
        return playerNames[playerId] || 'Loading...';
      }
      return 'Unknown';
    }).join(', ');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-slate-200 p-4 md:p-8 font-sans">
      <div className="w-full max-w-4xl text-center">
        {currentRoom && isMultiplayer && (
          <div className="mb-6 p-4 bg-slate-800/70 rounded-lg border border-cyan-500/50">
            <h2 className="text-2xl font-orbitron text-cyan-300 mb-2">Multiplayer Room: {currentRoom.name}</h2>
            <p className="text-slate-400 text-sm">Cartridge: <strong className="text-amber-400">{selectedCartridge?.name || 'Default'}</strong> by {selectedCartridge?.author || 'System'}</p>
            <p className="text-slate-400 text-sm">Players: {currentRoom.currentPlayers?.length || 0} / {currentRoom.maxPlayers}</p>
            <div className="mt-2 text-xs text-slate-500">
              Player List: {getPlayerNamesText()}
            </div>
          </div>
        )}
        
        {/* Hero Image */}
        <div className="mb-8 mx-auto max-w-4xl animate-fade-in-scale">
          {loadingHeroImage ? (
            <div className="aspect-[2/1] glassmorphism-strong rounded-lg flex items-center justify-center neon-border animate-pulse-glow">
              <LoadingSpinnerIcon className="w-12 h-12 text-cyan-400 animate-spin" />
              <span className="ml-3 text-slate-400 text-lg-responsive">Generating scene...</span>
            </div>
          ) : heroImageUrl ? (
            <img 
              src={heroImageUrl} 
              alt={`${cartridgeName} scene`}
              className="w-full aspect-[2/1] object-cover rounded-lg neon-border shadow-2xl hover:scale-[1.02] transition-transform duration-300"
            />
          ) : (
            <div className="aspect-[2/1] glassmorphism-strong rounded-lg flex items-center justify-center neon-border floating">
              <div className="text-center">
                <h2 className="text-2xl font-orbitron text-cyan-400 neon-text-cyan mb-2">Neo-Veridia Awaits</h2>
                <p className="text-slate-400 text-lg-responsive">Enter the digital dreamscape</p>
              </div>
            </div>
          )}
        </div>

        {worldMapEnabled && (
          <div className="mb-8">
            <div className="text-left">
              <h3 className="text-sm uppercase tracking-[0.4em] text-cyan-300">World Director Console</h3>
              <p className="text-xs text-slate-300 mt-2 max-w-2xl">
                Prototype spatial view powered by the autonomous backend. Faction pulses, companion telemetry, and emergent events render in real time to guide narrative curation.
              </p>
            </div>
            <div className="mt-3 rounded-xl border border-cyan-500/40 bg-slate-900/60 p-4">
              <WorldMapCanvas />
            </div>
          </div>
        )}
        
        <h1 className="text-5xl md:text-6xl font-orbitron font-bold text-cyan-400 neon-text-cyan mb-4 animate-slide-in floating">
          {cartridgeName}
        </h1>
        {!isMultiplayer && (
          <p className="text-slate-300 text-lg mb-1 leading-relaxed text-base-responsive">
             Echoes of the Void
          </p>
        )}
        {userProfile && ( // Only show if userProfile is loaded
          <p className="text-slate-400 text-md mb-8 leading-relaxed text-base-responsive">
            Welcome, <strong className="text-fuchsia-400 neon-text-fuchsia">{userProfile.name}</strong>. You are a Lucid Dreamer, plunging into Neo-Veridia's corrupted subconscious. Here, reality bleeds and your choices sculpt the dream.
          </p>
        )}
        
        <div className="my-6">
          <h3 className="text-2xl font-orbitron text-slate-100 mb-4">AI Constructs Online:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {(companions || []).map(comp => (
              <CompanionDisplay key={comp.id} companion={comp} archetype={selectedArchetype} userProfile={userProfile} />
            ))}
          </div>
        </div>

        <h2 className="text-3xl font-orbitron text-slate-100 mb-6">
          {isMultiplayer ? 'Select Your Character Class:' : 'Select Your Dreamer Archetype:'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {(displayArchetypes || []).map((archetype) => {
            return (
              <button
                key={archetype.id}
                onClick={() => setSelectedArchetype(archetype)}
                aria-pressed={selectedArchetype?.id === archetype.id}
                className={`p-6 rounded-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 cyber-button
                            ${selectedArchetype?.id === archetype.id 
                              ? 'glassmorphism-strong ring-2 ring-cyan-400 neon-border animate-pulse-glow' 
                              : 'glassmorphism hover:glassmorphism-strong ring-1 ring-slate-600'
                            }
                            border border-slate-700 hover:border-cyan-500/70 flex flex-col items-center h-full`}
              >
                <RobotIcon className="w-12 h-12 mx-auto mb-3 text-cyan-300" />
                <h3 className="text-xl font-orbitron text-slate-50 mb-2">{archetype.name}</h3>
                <p className="text-sm text-slate-300 flex-grow text-base-responsive">{archetype.description}</p>
              </button>
            );
          })}
        </div>

        {selectedArchetype && apiKeyStatus === 'exists' && (
          <div className="flex flex-col items-center justify-center space-y-4">
            <button
                onClick={async () => {
                  setIsStartingGame(true);
                  try {
                    await onStartGame(selectedArchetype);
                  } catch (error) {
                    console.error('Failed to start game:', error);
                    setIsStartingGame(false);
                  }
                }}
                className="px-12 py-4 bg-gradient-to-r from-fuchsia-600 to-cyan-600 text-white font-bold text-xl rounded-lg hover:from-fuchsia-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-fuchsia-400 shadow-xl cyber-button neon-border animate-pulse-glow"
                disabled={apiKeyStatus !== 'exists' || isStartingGame} // Disable while loading
            >
                {isStartingGame ? (
                  <span className="flex items-center gap-2">
                    <LoadingSpinnerIcon className="animate-spin" />
                    Initializing Dream Sequence...
                  </span>
                ) : (
                  isMultiplayer ? `Join Adventure as ${selectedArchetype.name}` : `Descend into the Dream as ${selectedArchetype.name}`
                )}
            </button>
            <p className="text-sm text-slate-400">Voice input will be available during gameplay.</p>
          </div>
        )}
        {apiKeyStatus !== 'exists' && (
             <p className="text-red-400 text-center mt-4">Cannot start game: API Key is not valid.</p>
        )}
      </div>
      <footer className="mt-12 text-center text-slate-600 text-sm">
        <p>&copy; {new Date().getFullYear()} Neo-Dream Weaver. Powered by Google Gemini & Imagen.</p>
         <p>This is a work of fiction. Any resemblance to actual events or persons, living or dead, is coincidental.</p>
      </footer>
    </div>
  );
};

export default MainScreen;
